import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

/**
 * Session 32 — NFC Solutions admin: per-business AI copilot controls.
 *   on/off · monthly budget (R, default 50; 0 = no limit) · OpenAI voice
 *   on/off · default voice · this month's usage + history.
 */
export const AdminAiCard = ({ adminHttp, headers, businessId, onEnabledChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [capDraft, setCapDraft] = useState("");

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const r = await adminHttp.get(`/admin/businesses/${businessId}/ai-usage`, { headers });
      setData(r.data);
      setCapDraft(String(r.data?.ai?.monthly_cap_zar ?? 50));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load AI usage.");
    } finally {
      setLoading(false);
    }
  }, [adminHttp, headers, businessId]);
  useEffect(() => { load(); }, [load]);

  const update = async (patch, okMsg) => {
    setSaving(true);
    try {
      const r = await adminHttp.patch(`/admin/businesses/${businessId}/ai-assistant`, patch, { headers });
      setData((d) => ({ ...(d || {}), ai: r.data?.ai || d?.ai, current: r.data?.usage || d?.current }));
      if (patch.enabled !== undefined) onEnabledChange && onEnabledChange(!!r.data?.ai_assistant_enabled);
      if (r.data?.ai?.monthly_cap_zar !== undefined) setCapDraft(String(r.data.ai.monthly_cap_zar));
      toast.success(okMsg || "AI settings updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update AI settings.");
    } finally {
      setSaving(false);
    }
  };

  const ai = data?.ai || {};
  const cur = data?.current || {};
  const used = Number(cur.used_zar || 0);
  const cap = Number(ai.monthly_cap_zar || 0);
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;

  return (
    <section className="rounded-[24px] border border-border bg-card p-5" data-testid="admin-ai-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]"><Sparkles className="h-5 w-5" /></span>
          <div>
            <p className="text-base font-semibold">AI Copilot</p>
            <p className="text-sm text-muted-foreground">GPT assistant on this business's Owner dashboard · brain {data?.model || "gpt-4.1-mini"} via the Emergent key{data?.openai_key_present ? " · OpenAI voice key present" : " · no OpenAI voice key"}</p>
          </div>
        </div>
        <label className="flex h-10 items-center gap-2 rounded-xl border border-border bg-background px-3 text-sm" data-testid="admin-ai-toggle-wrap">
          <span className="font-medium">{ai.enabled ? "On" : "Off"}</span>
          <Switch checked={!!ai.enabled} disabled={saving || loading} onCheckedChange={(v) => update({ enabled: v }, v ? "AI Copilot enabled for this business." : "AI Copilot disabled for this business.")} data-testid="admin-ai-toggle" />
        </label>
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading usage…</p>
      ) : (
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          {/* usage */}
          <div className="rounded-2xl border border-border bg-background p-4 lg:col-span-1" data-testid="admin-ai-usage">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">This month ({cur.month})</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums" data-testid="admin-ai-used">R{used.toFixed(2)}{cap > 0 ? <span className="text-sm font-normal text-muted-foreground"> / R{cap.toFixed(0)}</span> : null}</p>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary"><div className={`h-full ${pct >= 90 ? "bg-destructive" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} /></div>
            <p className="mt-2 text-xs text-muted-foreground">{cur.requests || 0} requests · {((cur.input_tokens || 0) + (cur.output_tokens || 0)).toLocaleString()} tokens · {cur.tts_requests || 0} spoken replies</p>
            <p className="text-xs text-muted-foreground">LLM ${Number(cur.llm_usd || 0).toFixed(4)} · voice ${Number(cur.tts_usd || 0).toFixed(4)} · rate R{cur.usd_zar || 18}/$</p>
            {cur.over_cap ? <Badge className="mt-2 bg-destructive text-destructive-foreground">Budget reached — assistant paused</Badge> : null}
          </div>

          {/* controls */}
          <div className="space-y-3 rounded-2xl border border-border bg-background p-4 lg:col-span-2">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="admin-ai-cap" className="text-xs font-medium">Monthly budget (R) · 0 = no limit</Label>
                <div className="mt-1 flex gap-2">
                  <Input id="admin-ai-cap" type="number" min="0" step="1" value={capDraft} onChange={(e) => setCapDraft(e.target.value)} className="h-10 rounded-xl" data-testid="admin-ai-cap-input" />
                  <Button type="button" variant="outline" className="h-10 rounded-xl" disabled={saving || String(Number(capDraft)) === String(cap)} onClick={() => update({ monthly_cap_zar: Math.max(0, Number(capDraft) || 0) }, "Monthly budget saved")} data-testid="admin-ai-cap-save">Save</Button>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">≈ R0.03 per command; R50 ≈ 1,500 commands. Spoken replies add ≈ R0.05 each.</p>
              </div>
              <div>
                <Label className="text-xs font-medium">Default voice</Label>
                <Select value={ai.voice || "nova"} onValueChange={(v) => update({ voice: v }, "Default voice saved")} disabled={saving}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl" data-testid="admin-ai-voice-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(data?.voices || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.label} — {v.hint}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-muted-foreground">The owner can also pick a voice from their AI settings.</p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium">OpenAI voice (spoken replies)</p>
                <p className="text-[11px] text-muted-foreground">Billed to your OpenAI account (~R0.27 per minute). Off = free device voice.</p>
              </div>
              <Switch checked={ai.tts_enabled !== false} disabled={saving} onCheckedChange={(v) => update({ tts_enabled: v }, v ? "OpenAI voice on" : "OpenAI voice off — device voice will be used")} data-testid="admin-ai-tts-toggle" />
            </div>
            {(data?.history || []).find((h) => h.last_tts_error) ? (
              <p className="text-[11px] text-amber-700 dark:text-amber-300" data-testid="admin-ai-tts-error">Last OpenAI voice error: {(data.history.find((h) => h.last_tts_error) || {}).last_tts_error}</p>
            ) : null}
            {(data?.history || []).length > 1 ? (
              <div className="text-xs text-muted-foreground">
                <p className="font-medium text-foreground">Previous months</p>
                <ul className="mt-1 grid gap-0.5 sm:grid-cols-2">
                  {data.history.slice(1).map((h) => <li key={h.month} className="flex justify-between"><span>{h.month}</span><span className="tabular-nums">R{Number(h.used_zar || 0).toFixed(2)} · {h.requests} req</span></li>)}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
};
